import React, { useEffect, useRef, useState } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterBastionFacility,
  CharacterContainer,
  CharacterItem,
  Money,
  SpellLimitation,
} from "@dndModels";
import { ALL_DRAGONS, CREATURE_TYPES } from "@dndModels/Constants";

import DataUtilities from '@dndUtilities/data_utilities';
import CharConnectUtilities from '@dndUtilities/char_connect_utilities';

import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import CharacterBastionAbilityInput from "@dndComponents/character/bastions/CharacterBastionAbilityInput";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import ButtonBox from "@components/input/buttons/ButtonBox";
import EditableLabel from "@components/input/strings/EditableLabel";
import DeleteButton from "@components/input/buttons/DeleteButton";
import ViewItem from "@dndComponents/display/ViewItem";
import SelectSpellBox from "@dndComponents/select/SelectSpellBox";

interface Props {
  char: Character;
  char_bf: CharacterBastionFacility;
  onDelete: () => void;
}

const connect_util = CharConnectUtilities.getInstance();
const data_util = DataUtilities.getInstance();

export default function CharacterBastionFacilityInput({
  char,
  char_bf,
  onDelete
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [show_desc, setShowDesc] = useState(false);
  const [show_contents, setShowContents] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [reloading_abilities, setReloadingAbilities] = useState(false);
  const [editing, setEditing] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading]);

  useEffect(() => {
    if (reloading_abilities) {
      setReloadingAbilities(false);
    }
  }, [reloading_abilities]);

  const bastionChanged = () => {
    setReloading(true);
  }
  
  useEffect(() => {
    if (!fetched) {
      char.subscription_holder.subscribe("Bastion", mounted, bastionChanged);
      setFetched(true);
    }
  }, [fetched, char]);

  const renderAbilities = () => {
    if (char_bf.source) {
      return (
        <>
          <Grid
            className="header centered"
            size={{
              xs: 12
            }}>
            Abilities
          </Grid>
          { !reloading_abilities && char_bf.source.abilities.map(ability => {
            return (
              <Grid
                key={ability.true_id}
                size={{
                  xs: 12
                }}>
                <CharacterBastionAbilityInput
                  char={char}
                  char_bf={char_bf}
                  bfa={ability}
                />
              </Grid>
            );
          })}
        </>
      );
    }
    return null;
  }

  const renderContents = () => {
    if (char_bf.source) {
      let contents_str = "Contents";
      if (char_bf.capacity > 0) {
        contents_str += " ("
        if (char_bf.items.length > 0) {
          contents_str += `${char_bf.items.length}`;
        } else if (char_bf.creatures.length > 0) {
          contents_str += `${char_bf.creatures.length}`;
        } else if (char_bf.customs.length > 0) {
          contents_str += `${char_bf.customs.map(o => o.count).reduce((a, b) => a + b, 0)}`;
        } else {
          contents_str += "0";
        }
        contents_str += `/${char_bf.capacity})`;
      } else if (char_bf.items.length > 0) {
        contents_str += ` (${char_bf.items.length})`;
      } else if (char_bf.creatures.length > 0) {
        contents_str += ` (${char_bf.creatures.length})`;
      } else if (char_bf.customs.length > 0) {
        contents_str += ` (${char_bf.customs.map(o => o.count).reduce((a, b) => a + b, 0)})`;
      } 
      if (contents_str !== "Contents") {
        return (
          <>
            <Grid
              className="header padded clickable"
              onClick={() => {
                setShowContents(!show_contents);
              }}
              size={{
                xs: 12
              }}>
              { contents_str }
            </Grid>
            { show_contents &&
              <>
                { renderContentCustoms() }
                { renderContentCreatures() }
                { renderContentItems() }
              </>
            }
          </>
        );
      }
    }
    return null;
  }

  const renderContentCustoms = () => {
    const profit_percent = 1 + char_bf.get_profit_percent(char.character_level) / 100;
    return (
      <>
        { char_bf.customs.map(custom => {
          return (
            <Grid
              key={custom.id}
              size={{
                xs: 12
              }}>
              <Grid container spacing={0} direction="row">
                <Grid
                  size={{
                    xs: 3
                  }}>
                  <EditableLabel 
                    name="Name"
                    value={custom.name}
                    onChange={(changed: string) => {
                      custom.name = changed;
                      char.changed();
                      char.subscription_holder.bastionChanged();
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 3
                  }}>
                  <Grid container spacing={0} direction="row">
                    { custom.count > 1 && 
                      <Grid
                        size={{
                          xs: 12
                        }}>
                        Count: { custom.count }
                      </Grid>
                    }
                    { custom.value > 0 && 
                      <Grid
                        size={{
                          xs: 12
                        }}>
                        Value: { custom.value }gp
                      </Grid>
                    }
                  </Grid>
                </Grid>
                <Grid
                  size={{
                    xs: 4
                  }}>
                  { custom.value > 0 && 
                    <ButtonBox
                      name={`Sell for ${custom.value * profit_percent}gp`}
                      onClick={() => {
                        char_bf.customs = char_bf.customs.filter(o => o.id !== custom.id);
                        const profit = new Money();
                        profit.copyGoldValue(custom.value * profit_percent);
                        char.money.add(profit);
                        char.changed();
                        char.subscription_holder.bastionChanged();
                        char.subscription_holder.itemsChanged();
                      }}
                    />
                  }
                </Grid>
                <Grid
                  size={{
                    xs: 2
                  }}>
                  <DeleteButton
                    onClick={() => {
                      char_bf.customs = char_bf.customs.filter(o => o.id !== custom.id);
                      char.changed();
                      char.subscription_holder.bastionChanged();
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          );
        })}
      </>
    );
  }

  const renderContentCreatures = () => {
    const profit_percent = 1 + char_bf.get_profit_percent(char.character_level) / 100;
    return (
      <>
        { char_bf.creatures.map(ci => {
          return (
            <Grid
              key={ci.true_id}
              size={{
                xs: 12
              }}>
              <Grid container spacing={0} direction="row">
                <Grid
                  size={{
                    xs: 3
                  }}>
                  <EditableLabel 
                    name="Name"
                    value={ci.name}
                    onChange={(changed: string) => {
                      ci.name = changed;
                      char.changed();
                      char.subscription_holder.bastionChanged();
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 2
                  }}>
                  { ci.value > 0 && 
                    <>
                      Value: { ci.value }gp
                    </>
                  }
                </Grid>
                <Grid
                  size={{
                    xs: 3
                  }}>
                  { ci.value > 0 && 
                    <ButtonBox
                      name={`Sell for ${ci.value * profit_percent}gp`}
                      onClick={() => {
                        char_bf.creatures = char_bf.creatures.filter(o => o.true_id !== ci.true_id);
                        const profit = new Money();
                        profit.copyGoldValue(ci.value * profit_percent);
                        char.money.add(profit);
                        char.changed();
                        char.subscription_holder.bastionChanged();
                        char.subscription_holder.itemsChanged();
                      }}
                    />
                  }
                </Grid>
                <Grid
                  size={{
                    xs: 3
                  }}>
                  { ci.value > 0 && 
                    <ButtonBox
                      name={`Move to Minions`}
                      onClick={() => {
                        char.minions.push(ci);
                        char_bf.creatures = char_bf.creatures.filter(o => o.true_id !== ci.true_id);
                        char.changed();
                        char.subscription_holder.bastionChanged();
                        char.subscription_holder.minionsChanged();
                      }}
                    />
                  }
                </Grid>
                <Grid
                  size={{
                    xs: 1
                  }}>
                  <DeleteButton
                    onClick={() => {
                      char_bf.creatures = char_bf.creatures.filter(o => o.true_id !== ci.true_id);
                      char.changed();
                      char.subscription_holder.bastionChanged();
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          );
        })}
      </>
    );
  }

  const renderContentItems = () => {
    return (
      <>
        { char_bf.items.map(char_item => {
          return (
            <Grid
              key={char_item.true_id}
              size={{
                xs: 12
              }}>
              <Grid container spacing={0} direction="row">
                <Grid
                  className="centered"
                  size={{
                    xs: 3
                  }}>
                  <ViewItem
                    item={char_item}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 6
                  }}>
                  <Grid container spacing={0} direction="row">
                    { char_item.count > 1 && 
                      <Grid
                        className="centered"
                        size={{
                          xs: 12
                        }}>x {char_item.count}</Grid> 
                    }
                    { char_item.extra !== "" &&
                      <Grid
                        size={{
                          xs: 12
                        }}>
                        { renderExtraOptions(char_item) }
                      </Grid> 
                    }
                  </Grid>
                </Grid>
                <Grid
                  size={{
                    xs: 2
                  }}>
                  <ButtonBox
                    name="Move to Inventory"
                    onClick={() => {
                      // Add to inventory
                      if (!(char_item instanceof CharacterContainer)) {
                        const base_item_id = char_item.base_item_id;
                        let good_containers = char.containers.filter(o => o.base_item_ids.includes(base_item_id));
                        if (good_containers.length === 0) {
                          const item_type = char_item.item_type;
                          good_containers = char.containers.filter(o => o.item_types.includes(item_type));
                        }
                        const backpack = char.containers.filter(o => o.name === "Backpack");
                        const equipment = char.containers.filter(o => o.name === "Equipment");
                        if (good_containers.length > 0) {
                          if (isNaN(good_containers[0].content_weight)) {
                            good_containers[0].content_weight = 0;
                          }
                          if (good_containers[0].max_weight === -1 || (good_containers[0].content_weight + char_item.weight) < good_containers[0].max_weight) {
                            char_item.in_char_container_id = good_containers[0].true_id;
                            good_containers[0].content_weight += char_item.weight;
                          } else if (backpack.length > 0 && (backpack[0].max_weight === -1 || (backpack[0].content_weight + char_item.weight) < backpack[0].max_weight)) {
                            char_item.in_char_container_id = backpack[0].true_id;
                            backpack[0].content_weight += char_item.weight;
                          } else {
                            char_item.in_char_container_id = "Equipment";
                            if (char_item.weight && char_item.weight > 0) {
                              equipment[0].content_weight += char_item.weight;
                            }
                          }
                        } else if (!["Weapon","Armor"].includes(char_item.item_type) && 
                          !char_item.item_type.includes("Focus") && 
                          !char_item.name.includes("Clothes") && 
                          backpack.length > 0 && 
                          (backpack[0].max_weight === -1 || 
                            (backpack[0].content_weight + char_item.weight) < backpack[0].max_weight
                          )
                        ) {
                          char_item.in_char_container_id = backpack[0].true_id;
                          backpack[0].content_weight += char_item.weight;
                        } else {
                          char_item.in_char_container_id = "Equipment";
                          if (char_item.weight && char_item.weight > 0) {
                            equipment[0].content_weight += char_item.weight;
                          }
                        }
                      }
                      char.items.push(char_item);
                      // Remove from Bastion Facility
                      char_bf.items = char_bf.items.filter(o => o.true_id !== char_item.true_id);
                      char.changed();
                      char.subscription_holder.itemsChanged();
                      char.subscription_holder.bastionChanged();
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 1
                  }}>
                  <DeleteButton
                    onClick={() => {
                      char_bf.items = char_bf.items.filter(o => o.true_id !== char_item.true_id);
                      char.changed();
                      char.subscription_holder.bastionChanged();
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          );
        })}
      </>
    );
  }

  const renderExtraOptions = (char_item: CharacterItem) => {
    const cmik = char_item.extra_mik;
    if (cmik && cmik.source) {
      const mik = cmik.source;
      if (mik.extra_base_type === "Spell") {
        const spell_limit = mik.extra_base_limit.limits.length > 0 && mik.extra_base_limit.limits[0] instanceof SpellLimitation ? mik.extra_base_limit.limits[0] : new SpellLimitation();
        return (
          <>
            <SelectSpellBox 
              name="Spell"
              value={cmik.extra}
              spell_limit={spell_limit}
              extra_options={["(Choose Later)"]}
              // character={char}
              // specific_spell_ids={spells.map(o => o._id)}
              onChange={async (changed: string) => {
                const spell = data_util.spells.find(o => o._id === changed);
                if (spell) {
                  cmik.extra = changed;
                  cmik.extra_spell = spell;
                  char_item.extra = changed;
                  char_item.extra_spell = spell;
                } else {
                  cmik.extra = "(Choose Later)";
                  cmik.extra_spell = null;
                  char_item.extra = "(Choose Later)";
                  char_item.extra_spell = null;
                }
                await connect_util.connectMagicItemKeyword({
                  char, 
                  char_item_or_id: char_item, 
                  magic_item_keyword_or_id: mik._id, 
                  source_id: "",
                  char_feature_id: ""
                });
                char.changed();
                char.subscription_holder.bastionChanged();
              }}
            />
          </>
        );
      } else {
        let extra_options: string[] = [];
        if (mik.extra_base_type === "Dragon Breed") {
          const extra_base_limits = mik.extra_base_limits as string[];
          extra_options = extra_base_limits.length > 0 ? extra_base_limits : ALL_DRAGONS;
        } else if (mik.extra_base_type === "Creature Type") {
          const extra_base_limits = mik.extra_base_limits as string[];
          extra_options = extra_base_limits.length > 0 ? extra_base_limits : CREATURE_TYPES;
        }
        return (
          <>
            <SelectStringBox
              name={mik.extra_base_type}
              options={extra_options}
              extra_options={["(Choose Later)"]}
              value={cmik.extra}
              onChange={(changed: string) => {
                cmik.extra = changed;
                char.changed();
                char.subscription_holder.bastionChanged();
              }}
            />
          </>
        );
      }
    }
    return null;
  }

  const renderEditing = () => {
    if (char_bf.source && char_bf.source.subtypes.length > 0) {
      return (
        <>
          <Grid
            size={{
              xs: 3
            }}>
            <StringBox
              name="Custom Name"
              value={char_bf._name}
              onBlur={(changed: string) => {
                char_bf._name = changed;
                char.changed();
                char.subscription_holder.bastionChanged();
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <SelectStringBox
              name="Subtype"
              options={char_bf.source.subtypes}
              value={char_bf.subtype}
              onChange={(changed: string) => {
                char_bf.subtype = changed;
                char.changed();
                char.subscription_holder.bastionChanged();
                setReloadingAbilities(true);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <DeleteButton
              label="Delete Facility"
              onClick={onDelete}
            />
          </Grid>
          { renderEnlarging() }
        </>
      );
    } else {
      return (
        <>
          <Grid
            size={{
              xs: 6
            }}>
            <StringBox
              name="Custom Name"
              value={char_bf._name}
              onBlur={(changed: string) => {
                char_bf._name = changed;
                char.changed();
                char.subscription_holder.bastionChanged();
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <DeleteButton
              label="Delete Facility"
              onClick={onDelete}
            />
          </Grid>
          { renderEnlarging() }
        </>
      );
    }
  }

  const renderEnlarging = () => {
    if (char_bf.source && 
      char_bf.source.size !== "Vast" && 
      char_bf.source.enlarge_vast_cost > 0
    ) {
      if (char_bf.source.size === "Cramped") {
        if (char_bf.size === "Cramped") {
          return (
            <>
              <Grid
                size={{
                  xs: 12
                }}>
                <ButtonBox
                  name={`Enlarge to Roomy for ${char_bf.source.enlarge_roomy_cost}gp`}
                  disabled={char.money.gold_value() < char_bf.source.enlarge_roomy_cost }
                  onClick={() => {
                    if (char_bf.source) {
                      char_bf._size = "Roomy";
                      const cost = new Money();
                      cost.copyGoldValue(char_bf.source.enlarge_roomy_cost);
                      char.money.subtract(cost);
                      char.changed();
                      char.subscription_holder.bastionChanged();
                    }
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12
                }}>
                <ButtonBox
                  name={`Enlarge to Vast for ${char_bf.source.enlarge_roomy_cost + char_bf.source.enlarge_vast_cost}gp`}
                  disabled={ char.money.gold_value() < (char_bf.source.enlarge_roomy_cost + char_bf.source.enlarge_vast_cost) }
                  onClick={() => {
                    if (char_bf.source) {
                      char_bf._size = "Vast";
                      const cost = new Money();
                      cost.copyGoldValue(char_bf.source.enlarge_roomy_cost + char_bf.source.enlarge_vast_cost);
                      char.money.subtract(cost);
                      char.changed();
                      char.subscription_holder.bastionChanged();
                    }
                  }}
                />
              </Grid>
            </>
          );
        } else if (char_bf.size === "Roomy") {
          return (
            <>
              <Grid
                size={{
                  xs: 12
                }}>
                <ButtonBox
                  name={`Revert to Cramped for ${char_bf.source.enlarge_roomy_cost}gp refund`}
                  onClick={() => {
                    if (char_bf.source) {
                      char_bf._size = "Cramped";
                      const cost = new Money();
                      cost.copyGoldValue(char_bf.source.enlarge_roomy_cost);
                      char.money.add(cost);
                      char.changed();
                      char.subscription_holder.bastionChanged();
                    }
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12
                }}>
                <ButtonBox
                  name={`Enlarge to Vast for ${char_bf.source.enlarge_vast_cost}gp`}
                  disabled={ char.money.gold_value() < char_bf.source.enlarge_vast_cost }
                  onClick={() => {
                    if (char_bf.source) {
                      char_bf._size = "Vast";
                      const cost = new Money();
                      cost.copyGoldValue(char_bf.source.enlarge_vast_cost);
                      char.money.subtract(cost);
                      char.changed();
                      char.subscription_holder.bastionChanged();
                    }
                  }}
                />
              </Grid>
            </>
          );
        } else if (char_bf.size === "Vast") {
          return (
            <>
              <Grid
                size={{
                  xs: 12
                }}>
                <ButtonBox
                  name={`Revert to Cramped for ${char_bf.source.enlarge_roomy_cost + char_bf.source.enlarge_vast_cost}gp refund`}
                  onClick={() => {
                    if (char_bf.source) {
                      char_bf._size = "Cramped";
                      const cost = new Money();
                      cost.copyGoldValue(char_bf.source.enlarge_roomy_cost + char_bf.source.enlarge_vast_cost);
                      char.money.add(cost);
                      char.changed();
                      char.subscription_holder.bastionChanged();
                    }
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12
                }}>
                <ButtonBox
                  name={`Revert to Roomy for ${char_bf.source.enlarge_vast_cost}gp refund`}
                  onClick={() => {
                    if (char_bf.source) {
                      char_bf._size = "Roomy";
                      const cost = new Money();
                      cost.copyGoldValue(char_bf.source.enlarge_vast_cost);
                      char.money.add(cost);
                      char.changed();
                      char.subscription_holder.bastionChanged();
                    }
                  }}
                />
              </Grid>
            </>
          );
        }
      } else {
        if (char_bf.size === "Roomy") {
          return (
            <>
              <Grid
                size={{
                  xs: 12
                }}>
                <ButtonBox
                  name={`Enlarge to Vast for ${char_bf.source.enlarge_vast_cost}gp`}
                  disabled={ char.money.gold_value() < char_bf.source.enlarge_vast_cost }
                  onClick={() => {
                    if (char_bf.source) {
                      char_bf._size = "Vast";
                      const cost = new Money();
                      cost.copyGoldValue(char_bf.source.enlarge_vast_cost);
                      char.money.subtract(cost);
                      char.changed();
                      char.subscription_holder.bastionChanged();
                    }
                  }}
                />
              </Grid>
            </>
          );
        } else if (char_bf.size === "Vast") {
          return (
            <>
              <Grid
                size={{
                  xs: 12
                }}>
                <ButtonBox
                  name={`Revert to Roomy for ${char_bf.source.enlarge_vast_cost}gp refund`}
                  onClick={() => {
                    if (char_bf.source) {
                      char_bf._size = "Roomy";
                      const cost = new Money();
                      cost.copyGoldValue(char_bf.source.enlarge_vast_cost);
                      char.money.add(cost);
                      char.changed();
                      char.subscription_holder.bastionChanged();
                    }
                  }}
                />
              </Grid>
            </>
          );
        }
      }
    }
    return null;
  }

  const renderDesc = () => {
    if (char_bf.source) {
      return (
        <>
          <Grid
            size={{
              xs: 12
            }}>
            <ToggleButtonBox
              name="Show Facility Description"
              value={show_desc}
              onToggle={() => { setShowDesc(!show_desc); }}
            />
          </Grid>
          { show_desc && 
            <Grid
              size={{
                xs: 12
              }}>
              <DisplayStringWithReplacements 
                display_string={char_bf.source.description} 
                cfs={char} 
              />
            </Grid>
          }
        </>
      );
    }
    return null;
  }

  return (
    <>
      <Grid
        size={{
          xs: 3
        }}>
        <ToggleButtonBox
          name="Edit"
          value={editing}
          onToggle={() => {
            setEditing(!editing);
          }}
        />
      </Grid>
      { editing ? 
        renderEditing()
      :
        <Grid
          size={{
            xs: 9
          }}>
        </Grid>
      }
      { char_bf.source &&
        <>
          { renderContents() }
          { renderAbilities() }
          { renderDesc() }
        </>
      }
    </>
  );
}
